import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface AppRow {
  readonly applicationId: string;
  readonly appCode: string;
  readonly appName: string;
  readonly appType: string;
  readonly lifecycleStatus: string;
  readonly currentEnvironment: string;
  readonly currentVersionNo: number;
  readonly publishScope: string;
}

const APP_TYPES = ['BUSINESS', 'DEPARTMENT', 'APPROVAL', 'SURVEY', 'CHECKLIST', 'REQUEST', 'COMPLIANCE', 'VENDOR', 'CAMPAIGN_EXTENSION', 'CUSTOM'];
const LIFECYCLE = ['DRAFT', 'IN_REVIEW', 'APPROVED', 'PUBLISHED', 'DEPRECATED', 'ARCHIVED'];
const SCOPES = ['PRIVATE', 'DEPARTMENT', 'TENANT', 'MARKETPLACE'];
const TARGET_ENVS = ['TEST', 'UAT', 'PROD'];
const PAGE = 25;

/**
 * M40 — Application Builder (§6/§18). The citizen developer's surface over the
 * application master (T523). Create a new low-code app, browse the catalogue,
 * request a deployment to the next environment, or archive an app (soft-delete,
 * LAW 3). Viewing needs APP_VIEW; create needs APP_CREATE; archive needs
 * APP_MANAGE; requesting a deployment needs APP_DEPLOY (SoD — BR-01 / BR-06).
 */
export function ApplicationBuilder(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<AppRow[]>([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [appName, setAppName] = useState('');
  const [appType, setAppType] = useState('BUSINESS');
  const [publishScope, setPublishScope] = useState('PRIVATE');
  const [description, setDescription] = useState('');
  const [deployEnv, setDeployEnv] = useState('TEST');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (typeFilter) params.set('appType', typeFilter);
    if (lifecycleFilter) params.set('lifecycleStatus', lifecycleFilter);
    if (search) params.set('search', search);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: AppRow[]; total: number }>(`/v1/app-platform/applications?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, typeFilter, lifecycleFilter, search, page, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!appName.trim()) { setActionError(t('m40.appBuilder.nameRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: AppRow }>(`/v1/app-platform/applications`, {
        method: 'POST',
        body: { appName: appName.trim(), appType, publishScope, description: description.trim() || undefined },
        token,
      });
      setNotice(t('m40.common.created', { code: r.data.appCode }));
      setAppName('');
      setDescription('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.appBuilder.notAuthCreate'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const deploy = async (row: AppRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/applications/${row.applicationId}/deploy`, {
        method: 'POST',
        body: { targetEnvironment: deployEnv },
        token,
      });
      setNotice(t('m40.appBuilder.deployRequested', { env: deployEnv, code: row.appCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.appBuilder.notAuthDeploy'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const archive = async (row: AppRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/applications/${row.applicationId}`, { method: 'DELETE', token });
      setNotice(t('m40.appBuilder.archived', { code: row.appCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.appBuilder.notAuthManage'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="application-builder">
      <h1>{t('m40.appBuilder.title')}</h1>

      <section aria-label="create-application">
        <h2>{t('m40.appBuilder.newApp')}</h2>
        <form aria-label="application-form" onSubmit={(e) => void create(e)}>
          <label>{t('m40.common.name')}<input aria-label="field-app-name" value={appName} onChange={(e) => setAppName(e.target.value)} /></label>
          <label>{t('m40.common.type')}
            <select aria-label="field-app-type" value={appType} onChange={(e) => setAppType(e.target.value)}>
              {APP_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m40.appBuilder.publishScope')}
            <select aria-label="field-publish-scope" value={publishScope} onChange={(e) => setPublishScope(e.target.value)}>
              {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>{t('m40.common.description')}<input aria-label="field-description" value={description} onChange={(e) => setDescription(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-application" disabled={busy}>{busy ? t('m40.common.creating') : t('m40.appBuilder.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="application-error">{actionError}</p>}
        {notice && <p aria-label="application-notice">{notice}</p>}
      </section>

      <section aria-label="application-filters">
        <label>{t('m40.common.type')}
          <select aria-label="filter-type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m40.common.allTypes')}</option>
            {APP_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('m40.common.lifecycle')}
          <select aria-label="filter-lifecycle" value={lifecycleFilter} onChange={(e) => { setLifecycleFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m40.appBuilder.allLifecycle')}</option>
            {LIFECYCLE.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
        <label>{t('m40.common.search')}<input aria-label="filter-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
        <label>{t('m40.appBuilder.deployTarget')}
          <select aria-label="deploy-env" value={deployEnv} onChange={(e) => setDeployEnv(e.target.value)}>
            {TARGET_ENVS.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m40.common.loading')}</p> : (
        <>
          <p aria-label="application-total">{t('m40.appBuilder.total', { total })}</p>
          <DataTable<AppRow>
            ariaLabel="application-table"
            testId="application-table"
            rowKey={(r) => r.applicationId}
            rowDataAttrs={(r) => ({ 'data-app': r.appCode })}
            emptyMessage={t('m40.appBuilder.empty')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m40.common.code'), render: (r) => r.appCode },
              { key: 'name', header: t('m40.common.name'), render: (r) => r.appName },
              { key: 'type', header: t('m40.common.type'), render: (r) => r.appType },
              { key: 'lifecycle', header: t('m40.common.lifecycle'), cellAriaLabel: (r) => `lifecycle-${r.appCode}`, render: (r) => r.lifecycleStatus },
              { key: 'environment', header: t('m40.common.environment'), cellAriaLabel: (r) => `env-${r.appCode}`, render: (r) => r.currentEnvironment },
              { key: 'scope', header: t('m40.common.scope'), cellAriaLabel: (r) => `scope-${r.appCode}`, render: (r) => r.publishScope },
              { key: 'actions', header: t('m40.common.actions'), render: (r) => (
                <>
                  <Button variant="primary" type="button" aria-label={`deploy-${r.appCode}`} onClick={() => void deploy(r)}>{t('m40.common.deploy')}</Button>
                  <Button variant="danger" type="button" aria-label={`archive-${r.appCode}`} onClick={() => void archive(r)}>{t('m40.appBuilder.archive')}</Button>
                </>
              ) },
            ]}
          />

          <nav aria-label="application-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m40.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m40.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m40.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
